<!--
  Client Portal v2 (PR D2): VERTICAL banded stacked-column chart.

  Replaces the PR-D pattern of one FULL-WIDTH horizontal BandStackedBar per
  bucket (each segmented by its OWN total), which showed funding MIX but lost
  VOLUME and made buckets non-comparable. Here every bucket is a VERTICAL
  column whose HEIGHT is the bucket total normalized to the MAX bucket total
  across the whole range -- a SHARED y-axis, so a big period visibly towers
  over a small one. Each column is internally split into the same 3 funding
  bands (budget / allowance / external) by that bucket's own mix, using the
  canonical BAND_DEFS colors so the legend reads identically everywhere.

  Reusable across the four banded surfaces (spend-over-time series,
  sales-by-store / -department / -period): pass any list of band buckets and
  the key that labels each one.
-->
<template>
  <div class="banded-columns" :data-testid="testid">
    <div
      v-if="!buckets || buckets.length === 0"
      class="banded-columns__empty text-body-2 text-medium-emphasis"
      :data-testid="`${testid}-empty`"
    >
      <slot name="empty">No data to chart yet.</slot>
    </div>
    <div
      v-else
      class="banded-columns__plot"
      :class="{ 'banded-columns__plot--dense': !!axisLabels }"
      :style="{ height: plotHeight }"
    >
      <div
        v-for="col in columns"
        :key="col.label"
        class="banded-columns__col-wrap"
      >
        <!-- The column grows up from the baseline; height is the SHARED-SCALE
             normalized total (this bucket / max bucket). -->
        <div
          class="banded-columns__col"
          :data-testid="`${testid}-column-${col.label}`"
          :style="{ height: col.heightPct + '%' }"
          role="img"
          :aria-label="col.ariaLabel"
        >
          <!-- Segments fill the column top-to-bottom in render order; each is
               a share of THIS bucket's total (so the column's internal split
               is the funding mix, while the column's overall height is the
               volume). -->
          <div
            v-for="seg in col.segments"
            :key="seg.key"
            class="banded-columns__segment"
            :data-testid="`band-segment-${seg.key}`"
            :style="{ height: seg.pct + '%', backgroundColor: seg.color }"
            :title="`${seg.label}: ${seg.pctLabel}%`"
          />
        </div>
        <div
          v-if="!axisLabels"
          class="banded-columns__label text-caption"
        >
          {{ col.label }}
        </div>
      </div>
    </div>
    <!-- Spread axis labels (e.g. "30 days ago / 15 days ago / Today") used in
         place of a per-column label when there are too many columns to label
         each one. -->
    <div
      v-if="axisLabels && buckets && buckets.length"
      class="banded-columns__axis text-caption"
      :data-testid="`${testid}-axis`"
    >
      <span
        v-for="(lbl, i) in axisLabels"
        :key="i"
        class="banded-columns__axis-label"
        >{{ lbl }}</span
      >
    </div>
  </div>
</template>

<script>
import { BAND_DEFS } from "./bandColors";

export default {
  name: "BandedColumnChart",
  props: {
    // Array of { <labelKey>, budget_cents, allowance_cents, external_cents,
    // total_cents }.
    buckets: {
      type: Array,
      default: () => [],
    },
    // Which field on each bucket labels the column (e.g. "period",
    // "store_name", "department_name").
    labelKey: {
      type: String,
      default: "period",
    },
    testid: {
      type: String,
      default: "banded-column-chart",
    },
    // Plot height (CSS length). The columns scale WITHIN this box.
    plotHeight: {
      type: String,
      default: "140px",
    },
    // Optional spread axis labels (e.g. ["30 days ago", "15 days ago",
    // "Today"]). When set, per-column labels are suppressed and these are laid
    // out space-between under the plot -- the right choice when there are too
    // many columns (e.g. 30 daily buckets) to label each individually.
    axisLabels: {
      type: Array,
      default: null,
    },
  },
  computed: {
    maxTotal() {
      // The shared denominator: the largest bucket total across the range.
      // Every column's height is measured against THIS, so buckets are
      // comparable at a glance. Guard against an all-zero range.
      return (this.buckets || []).reduce(
        (max, b) => Math.max(max, this.bucketTotal(b)),
        0
      );
    },
    columns() {
      const max = this.maxTotal;
      return (this.buckets || []).map((b) => {
        const total = this.bucketTotal(b);
        // Height as a share of the SHARED max (not this bucket's own total).
        const heightPct = max > 0 ? (total / max) * 100 : 0;
        const segments = this.segmentsFor(b, total);
        return {
          label: String(b[this.labelKey] ?? ""),
          heightPct,
          total,
          segments,
          ariaLabel:
            segments.map((s) => `${s.label} ${s.pctLabel}%`).join(", ") ||
            "no funded sales",
        };
      });
    },
  },
  methods: {
    bucketTotal(b) {
      if (!b) return 0;
      if (typeof b.total_cents === "number" && b.total_cents > 0) {
        return b.total_cents;
      }
      return (
        (b.budget_cents || 0) +
        (b.allowance_cents || 0) +
        (b.external_cents || 0)
      );
    },
    segmentsFor(b, total) {
      if (total <= 0) return [];
      return BAND_DEFS.map((def) => {
        const cents = b[def.centsKey] || 0;
        const pct = (cents / total) * 100;
        return {
          key: def.key,
          label: def.label,
          color: def.color,
          cents,
          pct,
          pctLabel: pct.toFixed(1),
        };
      }).filter((seg) => seg.cents > 0);
    },
  },
};
</script>

<style scoped>
.banded-columns {
  width: 100%;
}
.banded-columns__plot {
  display: flex;
  align-items: flex-end;
  gap: var(--tf-sp-2, 8px);
  border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.banded-columns__col-wrap {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
}
.banded-columns__col {
  width: 70%;
  max-width: 48px;
  min-height: 2px;
  display: flex;
  flex-direction: column;
  border-radius: var(--tf-radius-sm, 4px) var(--tf-radius-sm, 4px) 0 0;
  overflow: hidden;
  background: var(--tf-surface-dark, #e8eff5);
}
.banded-columns__segment {
  width: 100%;
}
/* Dense mode (e.g. 30 daily buckets): tighter gap + full-width columns so the
   bars read as a continuous daily activity strip. */
.banded-columns__plot--dense {
  gap: 2px;
}
.banded-columns__plot--dense .banded-columns__col {
  width: 100%;
  max-width: none;
  border-radius: 2px 2px 0 0;
}
.banded-columns__axis {
  display: flex;
  justify-content: space-between;
  margin-top: var(--tf-sp-1, 4px);
  color: var(--tf-fg-4, #757575);
}
.banded-columns__label {
  margin-top: var(--tf-sp-1, 4px);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  color: var(--tf-fg-4, #757575);
}
</style>
